import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Paperclip, FolderOpen, FileText, X, Loader2 } from "lucide-react";

const STATI = { elaborazione: "analisi in corso…", pronto: "analizzato", confermato: "analizzato", errore: "errore di lettura" };

// Allegato per l'assistente: upload su Smart Import o scelta di un documento già caricato
export const AiDocAttach = ({ doc, setDoc }) => {
  const fileRef = useRef(null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!doc || doc.stato !== "elaborazione") return undefined;
    const t = setInterval(() => api.get(`/ai/smartimport/${doc.id}`).then(({ data }) => {
      if (data.stato !== "elaborazione") setDoc((d) => (d && d.id === data.id ? { ...d, stato: data.stato } : d));
    }).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [doc, setDoc]);

  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/ai/smartimport", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc({ id: data.id, nome: data.original_filename, stato: data.stato });
      toast.success("Documento caricato in Smart Import");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    if (fileRef.current) fileRef.current.value = "";
  };
  const openList = (o) => { setOpen(o); if (o) api.get("/ai/smartimport").then(({ data }) => setList(data.slice(0, 15))).catch(() => setList([])); };

  return (
    <>
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="ai-attach-input" />
      <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} title="Allega PDF polizza" data-testid="ai-attach-btn"><Paperclip className="h-4 w-4" /></Button>
      <Popover open={open} onOpenChange={openList}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" title="Scegli da Smart Import" data-testid="ai-attach-pick-btn"><FolderOpen className="h-4 w-4" /></Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="start" data-testid="ai-attach-list">
          <p className="px-3 py-2 text-xs font-medium text-slate-500 border-b">Documenti in Smart Import</p>
          <div className="max-h-72 overflow-y-auto">
            {list.length === 0 ? <p className="px-3 py-3 text-sm text-slate-400">Nessun documento.</p> : list.map((d) => (
              <button key={d.id} onClick={() => { setDoc({ id: d.id, nome: d.original_filename, stato: d.stato }); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-slate-100 last:border-0" data-testid={`ai-attach-item-${d.id}`}>
                <div className="text-sm text-slate-800 truncate">{d.original_filename}</div>
                <div className="text-[11px] text-slate-500">{new Date(d.created_at).toLocaleDateString("it-IT")} · {d.operator_name || "—"} · {STATI[d.stato] || d.stato}</div>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

export const AiDocChip = ({ doc, onClear }) => (
  <div className="max-w-3xl mx-auto mb-2 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs text-violet-800" data-testid="ai-doc-chip">
    {doc.stato === "elaborazione" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
    <span className="truncate max-w-[260px]">{doc.nome}</span>
    <span className="text-violet-500" data-testid="ai-doc-stato">· {STATI[doc.stato] || doc.stato}</span>
    <button onClick={onClear} className="text-violet-400 hover:text-violet-700" aria-label="Rimuovi allegato" data-testid="ai-doc-clear"><X className="h-3.5 w-3.5" /></button>
  </div>
);
